import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Select,
  Checkbox,
  Input,
  RadioButton,
  TextArea,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldText,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import {
  getCarePlanDetails,
  getClientCarePlansPromise,
  getMedications,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function NVTransitionv2(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [carePlans, setCarePlans] = useState();
  const [initialRecordsProcessed, setInitialRecordsProcessed] = useState(false);
  const [toggleOff, setToggleOff] = useState(false);

  const getCarePlanPresentingProblems = useCallback(
    async (programId, dataObj) => {
      console.log("dataObj", dataObj);
      let presentingProblem = "";

      const carePlansBySelectedProgram = carePlans?.filter(
        (carePlan) => getFieldText(carePlan.ccx_clientprogram) === programId
      );

      const selectedCarePlan = carePlansBySelectedProgram?.sort((a, b) => {
        const aDate = moment(getFieldDate(a.ccx_effectivedate));
        const bDate = moment(getFieldDate(b.ccx_effectivedate));

        return aDate.isAfter(bDate) ? -1 : aDate.isBefore(bDate) ? 1 : 0;
      })?.[0];

      if (selectedCarePlan !== undefined) {
        const carePlanDetails = await getCarePlanDetails(
          selectedCarePlan.ccx_careplanid
        );

        if (!isMountedRef.current) {
          return;
        }

        presentingProblem = carePlanDetails.plannarrative.filter(
          (narrative) => narrative.ccx_FieldName === "TIPP"
        )[0];
      }

      if (dataObj) {
        dataObj.EmotionalDetails["@value"] = presentingProblem?.ccx_Value;
        console.log("dataObjPost", dataObj);
        setData(dataObj);
        setToggleOff(true);
        setToggleOff(false);
      }

      return presentingProblem?.ccx_Value;
    },
    [carePlans]
  );

  useEffect(() => {
    if (
      submissionid ||
      !clientid ||
      loading ||
      loaded ||
      Object.keys(data).length === 0
    ) {
      return;
    }

    setLoading(true);
    const loadClientRecords = async () => {
      const results = await Promise.all([
        getClientCarePlansPromise(clientid),
        getMedications(clientid),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      setCarePlans(results[0]);
      const currentMedications = results[1]
        .filter((med) => {
          return (
            med.ccx_stopdate === undefined ||
            new Date(getFieldText(med.ccx_stopdate)) > new Date()
          );
        })
        .map((med) =>
          med.ccx_name === "No Medications"
            ? "No Medications"
            : `${med.ccx_name} ${getFieldValue(med.ccx_dose)}${getFieldName(
                med.ccx_doseunit_global
              )} ${
                med.ccx_doseother ? getFieldName(med.ccx_doseother) : ""
              } - ${med.ccx_prescriber}`
        ).join(`
`);

      const obj = { ...data };
      obj.PhysicalDetails["@value"] = currentMedications;
      setData(obj);
      setLoaded(true);
      setLoading(false);
    };

    loadClientRecords();
  }, [submissionid, clientid, loading, loaded, data]);

  useEffect(() => {
    if (
      !carePlans ||
      initialRecordsProcessed ||
      Object.keys(data).length === 0
    ) {
      return;
    }

    if (carePlans.length === 0 || !initialProgram) {
      setInitialRecordsProcessed(true);
      return;
    }

    const loadCarePlanProblems = async () => {
      const presentingProblems = await getCarePlanPresentingProblems(
        initialProgram.ccx_client_programid
      );

      if (!isMountedRef.current) {
        return;
      }

      if (presentingProblems) {
        const obj = { ...data };
        obj.EmotionalDetails["@value"] = presentingProblems;
        setData(obj);
      }

      setInitialRecordsProcessed(true);
    };

    loadCarePlanProblems();
  }, [
    initialProgram,
    data,
    initialRecordsProcessed,
    carePlans,
    getCarePlanPresentingProblems,
  ]);

  const handleSelectedProgramChange = async (e) => {
    const obj = handleChange(e);
    const input = e.currentTarget;

    const selectedProgram = clientData.programs.filter(
      (option) => option.ccx_client_programid === input.value
    )[0];

    if (!selectedProgram) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = selectedProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(selectedProgram.ccx_programunit);
    setFormsyDetails(details);

    console.log("dataObjPre", obj);
    getCarePlanPresentingProblems(selectedProgram.ccx_client_programid, obj);

    // if (!isMountedRef.current) {
    //   return;
    // }

    // obj.EmotionalDetails["@value"] = presentingProblems;
    // setData(obj);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration?.[0]
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Input
                    name="SAFYContact"
                    label={data.SAFYContact["@label"]}
                    error={errors.SAFYContact}
                    value={getFieldValue(data.SAFYContact)}
                    onChange={handleChange}
                    identical={compare("SAFYContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <RadioButton
                    name="DischargeReason"
                    label={data.DischargeReason["@label"]}
                    error={errors.DischargeReason}
                    value={getFieldValue(data.DischargeReason)}
                    options={JSON.parse(
                      data.DischargeReason["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="TimeFrame"
                    label={data.TimeFrame["@label"]}
                    error={errors.TimeFrame}
                    value={getFieldValue(data.TimeFrame)}
                    onChange={handleChange}
                    identical={compare("TimeFrame")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="TransitioningTo"
                    label={data.TransitioningTo["@label"]}
                    error={errors.TransitioningTo}
                    value={getFieldValue(data.TransitioningTo)}
                    onChange={handleChange}
                    identical={compare("TransitioningTo")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="h5">Needs Explained to New Caregiver</div>
              {!toggleOff ? (
                <TextArea
                  name="EmotionalDetails"
                  label={data.EmotionalDetails["@label"]}
                  error={errors.EmotionalDetails}
                  value={data.EmotionalDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("EmotionalDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <div className="row">
                <div className="col">
                  <Input
                    name="EmotionalPerson"
                    label={data.EmotionalPerson["@label"]}
                    error={errors.EmotionalPerson}
                    value={getFieldValue(data.EmotionalPerson)}
                    onChange={handleChange}
                    identical={compare("EmotionalPerson")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="EmotionalDone"
                    label={data.EmotionalDone["@label"]}
                    checked={getFieldValue(data.EmotionalDone) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
              </div>
              <TextArea
                name="PhysicalDetails"
                label={data.PhysicalDetails["@label"]}
                error={errors.PhysicalDetails}
                value={getFieldValue(data.PhysicalDetails)}
                onChange={handleChange}
                identical={compare("PhysicalDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="PhysicalPerson"
                    label={data.PhysicalPerson["@label"]}
                    error={errors.PhysicalPerson}
                    value={getFieldValue(data.PhysicalPerson)}
                    onChange={handleChange}
                    identical={compare("PhysicalPerson")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="PhysicalDone"
                    label={data.PhysicalDone["@label"]}
                    checked={getFieldValue(data.PhysicalDone) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
              </div>
              <TextArea
                name="AppointmentsDetails"
                label={data.AppointmentsDetails["@label"]}
                error={errors.AppointmentsDetails}
                value={getFieldValue(data.AppointmentsDetails)}
                onChange={handleChange}
                identical={compare("AppointmentsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="AppointmentsPerson"
                    label={data.AppointmentsPerson["@label"]}
                    error={errors.AppointmentsPerson}
                    value={getFieldValue(data.AppointmentsPerson)}
                    onChange={handleChange}
                    identical={compare("AppointmentsPerson")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="AppointmentsDone"
                    label={data.AppointmentsDone["@label"]}
                    checked={getFieldValue(data.AppointmentsDone) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
              </div>
              <Input
                name="HotlineDetails"
                label={data.HotlineDetails["@label"]}
                error={errors.HotlineDetails}
                value={getFieldValue(data.HotlineDetails)}
                onChange={handleChange}
                identical={compare("HotlineDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="HotlinePerson"
                    label={data.HotlinePerson["@label"]}
                    error={errors.HotlinePerson}
                    value={getFieldValue(data.HotlinePerson)}
                    onChange={handleChange}
                    identical={compare("HotlinePerson")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="HotlineDone"
                    label={data.HotlineDone["@label"]}
                    checked={getFieldValue(data.HotlineDone) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
              </div>
              <div className="h5">First Visit or Respite</div>
              <TextArea
                name="InitialTransportDetails"
                label={data.InitialTransportDetails["@label"]}
                error={errors.InitialTransportDetails}
                value={getFieldValue(data.InitialTransportDetails)}
                onChange={handleChange}
                identical={compare("InitialTransportDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="WhereVisitDetails"
                label={data.WhereVisitDetails["@label"]}
                error={errors.WhereVisitDetails}
                value={getFieldValue(data.WhereVisitDetails)}
                onChange={handleChange}
                identical={compare("WhereVisitDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="VisitLengthDetails"
                label={data.VisitLengthDetails["@label"]}
                error={errors.VisitLengthDetails}
                value={getFieldValue(data.VisitLengthDetails)}
                onChange={handleChange}
                identical={compare("VisitLengthDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="TeamMemberPresentDetails"
                label={data.TeamMemberPresentDetails["@label"]}
                error={errors.TeamMemberPresentDetails}
                value={getFieldValue(data.TeamMemberPresentDetails)}
                onChange={handleChange}
                identical={compare("TeamMemberPresentDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FirstVisitPerson"
                label={data.FirstVisitPerson["@label"]}
                error={errors.FirstVisitPerson}
                value={getFieldValue(data.FirstVisitPerson)}
                onChange={handleChange}
                identical={compare("FirstVisitPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="VisitDone"
                label={data.VisitDone["@label"]}
                checked={getFieldValue(data.VisitDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Subsequent Visits or Respite</div>
              <TextArea
                name="ContactsDetails"
                label={data.ContactsDetails["@label"]}
                error={errors.ContactsDetails}
                value={getFieldValue(data.ContactsDetails)}
                onChange={handleChange}
                identical={compare("ContactsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="OvernightsDetails"
                label={data.OvernightsDetails["@label"]}
                error={errors.OvernightsDetails}
                value={getFieldValue(data.OvernightsDetails)}
                onChange={handleChange}
                identical={compare("OvernightsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SubsequentVisitsPerson"
                label={data.SubsequentVisitsPerson["@label"]}
                error={errors.SubsequentVisitsPerson}
                value={getFieldValue(data.SubsequentVisitsPerson)}
                onChange={handleChange}
                identical={compare("SubsequentVisitsPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="SubsequentVisitsDone"
                label={data.SubsequentVisitsDone["@label"]}
                checked={getFieldValue(data.SubsequentVisitsDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Child Informed of Transition</div>
              <Input
                name="WhenInformed"
                label={data.WhenInformed["@label"]}
                error={errors.WhenInformed}
                value={getFieldValue(data.WhenInformed)}
                onChange={handleChange}
                identical={compare("WhenInformed")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WhenInformedPerson"
                label={data.WhenInformedPerson["@label"]}
                error={errors.WhenInformedPerson}
                value={getFieldValue(data.WhenInformedPerson)}
                onChange={handleChange}
                identical={compare("WhenInformedPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="WhenInformedDone"
                label={data.WhenInformedDone["@label"]}
                checked={getFieldValue(data.WhenInformedDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Child's Well-being</div>
              {data.TransitionNeedsDetails["@label"]}
              <br />
              <i>
                Please include input from the youth in this section. What is
                hard about moving to a new home? What do you have a hard time
                verbalizing to your new parent? Etc.
              </i>
              <TextArea
                name="TransitionNeedsDetails"
                error={errors.TransitionNeedsDetails}
                value={getFieldValue(data.TransitionNeedsDetails)}
                onChange={handleChange}
                identical={compare("TransitionNeedsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="TransitionNeedsPerson"
                label={data.TransitionNeedsPerson["@label"]}
                error={errors.TransitionNeedsPerson}
                value={getFieldValue(data.TransitionNeedsPerson)}
                onChange={handleChange}
                identical={compare("TransitionNeedsPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="TransitionNeedsDone"
                label={data.TransitionNeedsDone["@label"]}
                checked={getFieldValue(data.TransitionNeedsDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              {data.RoutineDetails["@label"]}
              <br />
              <i>
                Examples: Bedtime routine, comfort items, meal routines…any
                routine behavior that makes the child more comfortable.
              </i>
              <TextArea
                name="RoutineDetails"
                error={errors.RoutineDetails}
                value={getFieldValue(data.RoutineDetails)}
                onChange={handleChange}
                identical={compare("RoutineDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="RoutinePerson"
                label={data.RoutinePerson["@label"]}
                error={errors.RoutinePerson}
                value={getFieldValue(data.RoutinePerson)}
                onChange={handleChange}
                identical={compare("RoutinePerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="RoutineDone"
                label={data.RoutineDone["@label"]}
                checked={getFieldValue(data.RoutineDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <TextArea
                name="WhoHelpsPackDetails"
                label={data.WhoHelpsPackDetails["@label"]}
                error={errors.WhoHelpsPackDetails}
                value={getFieldValue(data.WhoHelpsPackDetails)}
                onChange={handleChange}
                identical={compare("WhoHelpsPackDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WhoHelpsPackPerson"
                label={data.WhoHelpsPackPerson["@label"]}
                error={errors.WhoHelpsPackPerson}
                value={getFieldValue(data.WhoHelpsPackPerson)}
                onChange={handleChange}
                identical={compare("WhoHelpsPackPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="WhoHelpsPackDone"
                label={data.WhoHelpsPackDone["@label"]}
                checked={getFieldValue(data.WhoHelpsPackDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Transition Day</div>
              <TextArea
                name="HelpGoodbyeDetails"
                label={data.HelpGoodbyeDetails["@label"]}
                error={errors.HelpGoodbyeDetails}
                value={getFieldValue(data.HelpGoodbyeDetails)}
                onChange={handleChange}
                identical={compare("HelpGoodbyeDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="HelpGoodbyePerson"
                label={data.HelpGoodbyePerson["@label"]}
                error={errors.HelpGoodbyePerson}
                value={getFieldValue(data.HelpGoodbyePerson)}
                onChange={handleChange}
                identical={compare("HelpGoodbyePerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="HelpGoodbyeDone"
                label={data.HelpGoodbyeDone["@label"]}
                checked={getFieldValue(data.HelpGoodbyeDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <TextArea
                name="TransportDetails"
                label={data.TransportDetails["@label"]}
                error={errors.TransportDetails}
                value={getFieldValue(data.TransportDetails)}
                onChange={handleChange}
                identical={compare("TransportDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="TransportPerson"
                label={data.TransportPerson["@label"]}
                error={errors.TransportPerson}
                value={getFieldValue(data.TransportPerson)}
                onChange={handleChange}
                identical={compare("TransportPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="TransportDone"
                label={data.TransportDone["@label"]}
                checked={getFieldValue(data.TransportDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Contact with Previous Caregiver</div>
              <Input
                name="ChildVisitDetails"
                label={data.ChildVisitDetails["@label"]}
                error={errors.ChildVisitDetails}
                value={getFieldValue(data.ChildVisitDetails)}
                onChange={handleChange}
                identical={compare("ChildVisitDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="RespiteDetails"
                label={data.RespiteDetails["@label"]}
                error={errors.RespiteDetails}
                value={getFieldValue(data.RespiteDetails)}
                onChange={handleChange}
                identical={compare("RespiteDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ContactPerson"
                label={data.ContactPerson["@label"]}
                error={errors.ContactPerson}
                value={getFieldValue(data.ContactPerson)}
                onChange={handleChange}
                identical={compare("ContactPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="ContactDone"
                label={data.ContactDone["@label"]}
                checked={getFieldValue(data.ContactDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <div className="h5">Paperwork</div>
              <Input
                name="DocumentsDetails"
                label={data.DocumentsDetails["@label"]}
                error={errors.DocumentsDetails}
                value={getFieldValue(data.DocumentsDetails)}
                onChange={handleChange}
                identical={compare("DocumentsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="DocumentsPerson"
                label={data.DocumentsPerson["@label"]}
                error={errors.DocumentsPerson}
                value={getFieldValue(data.DocumentsPerson)}
                onChange={handleChange}
                identical={compare("DocumentsPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="DocumentsDone"
                label={data.DocumentsDone["@label"]}
                checked={getFieldValue(data.DocumentsDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Input
                name="EmailDFSDetails"
                label={data.EmailDFSDetails["@label"]}
                error={errors.EmailDFSDetails}
                value={getFieldValue(data.EmailDFSDetails)}
                onChange={handleChange}
                identical={compare("EmailDFSDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="EmailDFSPerson"
                label={data.EmailDFSPerson["@label"]}
                error={errors.EmailDFSPerson}
                value={getFieldValue(data.EmailDFSPerson)}
                onChange={handleChange}
                identical={compare("EmailDFSPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="EmailDFSDone"
                label={data.EmailDFSDone["@label"]}
                checked={getFieldValue(data.EmailDFSDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Input
                name="EmailCensusDetails"
                label={data.EmailCensusDetails["@label"]}
                error={errors.EmailCensusDetails}
                value={getFieldValue(data.EmailCensusDetails)}
                onChange={handleChange}
                identical={compare("EmailCensusDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="EmailCensusPerson"
                label={data.EmailCensusPerson["@label"]}
                error={errors.EmailCensusPerson}
                value={getFieldValue(data.EmailCensusPerson)}
                onChange={handleChange}
                identical={compare("EmailCensusPerson")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="EmailCensusDone"
                label={data.EmailCensusDone["@label"]}
                checked={getFieldValue(data.EmailCensusDone) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default NVTransitionv2;
